// Belts and their exams. Everyone starts in white; the exams run red,
// yellow, orange, green, blue, purple, brown, black. Exam i promotes from
// BELTS[i] to BELTS[i + 1]; each early one brings in one new opponent type,
// weighted up so it gets practised. Names live in the i18n files
// ('belt.<id>' and 'color.<id>').

import type { RoundSpec } from '@/sim/sim';
import type { BossId, EnemyKind } from '@/sim/tuning';

export type BeltId =
  'white' | 'red' | 'yellow' | 'orange' | 'green' | 'blue' | 'purple' | 'brown' | 'black';

export interface Belt {
  id: BeltId;
  color: string;
  /** Darker second tone, so light belts still read on a white gi. */
  edge: string;
}

export const BELTS: readonly Belt[] = [
  { id: 'white', color: '#f5f5f0', edge: '#c9c6bc' },
  { id: 'red', color: '#d62828', edge: '#9b1c1c' },
  { id: 'yellow', color: '#ffd23f', edge: '#c9a21c' },
  { id: 'orange', color: '#ff8c32', edge: '#c9651a' },
  { id: 'green', color: '#3fae4f', edge: '#2a7d37' },
  { id: 'blue', color: '#2f6fd6', edge: '#1f4d99' },
  { id: 'purple', color: '#8a4fd1', edge: '#613593' },
  { id: 'brown', color: '#7a4a26', edge: '#54321a' },
  { id: 'black', color: '#1c1b1f', edge: '#000000' },
];

export const BLACK = BELTS.length - 1;

export interface Exam {
  /** The belt earned by passing. */
  to: number;
  rounds: RoundSpec[];
  /** The opponent type this exam introduces, shown on its intro card. */
  introduces?: EnemyKind;
  /** The stage boss who ends it, if it's its stage's last exam. */
  boss?: BossId;
}

type Mix = Partial<Record<EnemyKind, number>>;

const MIXES: { introduces?: EnemyKind; mix: Mix }[] = [
  { introduces: 'basic', mix: { basic: 1 } }, // red
  { introduces: 'heavy', mix: { basic: 3, heavy: 2 } }, // yellow
  { introduces: 'feinter', mix: { basic: 3, heavy: 1, feinter: 2 } }, // orange
  { introduces: 'blocker', mix: { basic: 3, heavy: 1, feinter: 1, blocker: 2 } }, // green
  { mix: { basic: 3, heavy: 2, feinter: 2, blocker: 2 } }, // blue
  { introduces: 'kicker', mix: { basic: 2, heavy: 1, feinter: 2, blocker: 2, kicker: 3 } }, // purple
  { mix: { basic: 2, heavy: 2, feinter: 2, blocker: 2, kicker: 2 } }, // brown
  {
    introduces: 'grappler',
    mix: { basic: 2, heavy: 1, feinter: 2, blocker: 2, kicker: 2, grappler: 3 },
  }, // black
];

/** Share of arrivals that are attack patterns, per exam: none in the very
 *  first, then more belt by belt (which ones depends on level and mix). */
const PATTERN_SHARE = [0, 0.15, 0.2, 0.25, 0.25, 0.3, 0.3, 0.35];

/** Each stage's last exam ends with its boss: the dojo's at orange, the
 *  park's at blue, the rooftop's at black. */
const BOSS_AT: Partial<Record<number, BossId>> = { 2: 'senpai', 4: 'captain', 7: 'ronin' };

/** Exam i (0-based) promotes to belt i + 1. Rounds get longer and faster. */
export const EXAMS: readonly Exam[] = MIXES.map(({ introduces, mix }, i) => {
  const boss = BOSS_AT[i];
  return {
    to: i + 1,
    ...(introduces ? { introduces } : {}),
    ...(boss ? { boss } : {}),
    rounds: [0, 1, 2].map((r) => ({
      level: Math.round(i * 1.5) + r,
      count: 6 + i * 2 + r * 2,
      ramp: false,
      kinds: mix,
      patterns: PATTERN_SHARE[i] ?? 0,
      ...(boss && r === 2 ? { boss } : {}),
    })),
  };
});

/** The daily exam is the same for everyone: three rounds at green-belt pace. */
export const DAILY_ROUNDS: readonly RoundSpec[] = [0, 1, 2].map((r) => ({
  level: 4 + r * 2,
  count: 10 + r * 3,
  ramp: false,
  kinds: { basic: 3, heavy: 2, feinter: 2, blocker: 2, kicker: 1 },
  patterns: 0.3,
}));

/**
 * Kumite, open from black belt: one endless round that starts at a level
 * just below the black-belt exam's pace (not from scratch), ramps a level
 * every few opponents and sends more attack patterns as it does.
 */
export const KUMITE: RoundSpec = { level: 6, count: null, ramp: true, patterns: 0.25 };

/** Stars for a passed exam, from the share of hits that were PERFECT or COUNTER. */
export function starsFor(perfects: number, hits: number): 1 | 2 | 3 {
  const ratio = hits > 0 ? perfects / hits : 0;
  return ratio >= 0.75 ? 3 : ratio >= 0.45 ? 2 : 1;
}

/** The fewest PERFECT (or COUNTER) hits out of `hits` that earn `stars`. */
export function perfectsFor(stars: number, hits: number): number {
  let perfects = 0;
  while (perfects < hits && starsFor(perfects, hits) < stars) perfects++;
  return perfects;
}

/** Mastery marks per exam (Save.marks bits), worth retaking a passed exam for:
 *  a hanko for passing without taking a hit, a gold stripe on the belt for a
 *  long PERFECT chain, longer belt by belt. */
export const MARK = { stamp: 1, stripe: 2 } as const;

export function stripeChain(exam: number): number {
  return 10 + 2 * exam;
}

export function marksFor(
  exam: number,
  run: { passed: boolean; hitsTaken: number; bestChain: number },
): number {
  if (!run.passed) return 0;
  return (
    (run.hitsTaken === 0 ? MARK.stamp : 0) | (run.bestChain >= stripeChain(exam) ? MARK.stripe : 0)
  );
}
